import {useMemo, useCallback, useEffect} from 'react'

import {Heading, Pagination} from '@primer/react'

/* eslint-disable-next-line no-restricted-imports */
import {useSearchParams} from '@github-ui/react-router'
import {announce} from '@github-ui/aria-live'

import type {ServersData, BaseServer} from '../../../../types'

import {GridSection} from '../GridSection/GridSection'

import styles from './Content.module.css'

type Props = {
  data: ServersData
}

export function Content({data}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()

  // v0.1 API uses 'search' parameter
  const searchParamKey = 'search'
  const urlSearchQuery = searchParams.get(searchParamKey) || ''
  const hasSearchTerm = urlSearchQuery.trim().length > 0

  // v0.1 API uses page-based pagination
  const currentPage = parseInt(searchParams.get('page') || '1', 10)

  // Memoized server normalization function
  const normalizeServer = useCallback(
    (server: BaseServer) => ({
      ...server,
      uses_custom_opengraph_image: server.uses_custom_opengraph_image || false,
    }),
    [],
  )

  // Transform and filter MCPs based on search state
  const {regularMcps, searchResults} = useMemo(() => {
    // Only use the data if it matches the current search state
    // This prevents showing stale search results when clearing the search
    const transformedMcps = (data.servers || []).map(normalizeServer)

    if (!hasSearchTerm) {
      return {
        regularMcps: transformedMcps,
        searchResults: [],
      }
    }

    // When searching, use server-provided results directly (no client-side filtering)
    return {
      regularMcps: [],
      searchResults: transformedMcps,
    }
  }, [data.servers, hasSearchTerm, normalizeServer])

  // Determine which MCPs to display
  const currentMcps = hasSearchTerm ? searchResults : regularMcps

  // Announce search results to screen readers for accessibility (WCAG 4.1.3)
  // Using a delay to ensure the live region is established before announcing
  useEffect(() => {
    if (hasSearchTerm && searchResults.length === 0) {
      const message = `No MCPs found matching "${urlSearchQuery}". Try a different search term.`
      // Delay announcement to ensure live region is ready after page navigation
      const timeoutId = setTimeout(() => {
        announce(message)
      }, 150)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [hasSearchTerm, searchResults.length, urlSearchQuery])

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(current => {
        const newSearchParams = new URLSearchParams(current)
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            newSearchParams.delete(key)
          } else {
            newSearchParams.set(key, value)
          }
        }
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  // v0.1 API page-based pagination handler
  const handlePageChange = useCallback(
    (event: React.MouseEvent, page: number) => {
      event.preventDefault()
      updateSearchParams({page: page.toString()})
    },
    [updateSearchParams],
  )

  // Pagination state
  const totalPages = data.metadata.total_pages || 1

  const heading = hasSearchTerm ? 'Search results' : 'All MCP servers'

  return (
    <div className={styles.container}>
      <div>
        {currentMcps.length > 0 ? (
          <div className="d-flex flex-items-center mb-4">
            <Heading as="h2" className="f3 mr-2">
              {heading}
            </Heading>

            <span className="Counter">{hasSearchTerm ? data.metadata.count : data.metadata.total}</span>
          </div>
        ) : (
          <Heading as="h2" className="f3 mb-4">
            {heading}
          </Heading>
        )}

        {currentMcps.length > 0 ? <GridSection mcps={currentMcps} /> : null}

        {hasSearchTerm && searchResults.length === 0 && (
          <p className="color-fg-muted mb-0">
            No MCPs found matching &quot;{urlSearchQuery}&quot;. Try a different search term.
          </p>
        )}
      </div>

      {/* v0.1 API: page-based pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Pagination
            pageCount={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            surroundingPageCount={2}
            aria-label="MCP Registry pagination"
          />
        </div>
      )}
    </div>
  )
}

try{ Content.displayName ||= 'Content' } catch {}